import type http from 'http';
import * as crypto from 'crypto';
import { verifyTypedData } from 'viem';
import {
  teamStore,
  agentKeyStore,
  walletToAgent,
  keyRegistry,
  persistKeyRegistry,
  teamMessageStore,
  teamPresenceStore,
  persistTeamDurable,
  persistAgentStore,
  challengeStore,
  reloadTeam,
} from '../state';
import {
  json,
  parseJsonBody,
  extractParam,
  getTeamMember,
  hasTeamPermission,
  requireTeamAccess,
  pruneStalePresence,
  normalizePresenceSurface,
  getPresenceTtlMs,
} from '../utils';
import {
  normalizeBearerCapabilities,
  normalizeBearerSurface,
  requireAuth,
  resolveRequestingAgent,
} from '../auth-utils';
import { broadcastToRoom } from '../team-room';
import { extractAndVerifySigning } from '../identity/signing-middleware';
import {
  advanceNegotiation,
  createNegotiation,
  getNegotiation,
  listNegotiationsForTeam,
  settleNegotiation,
  type NegotiationAction,
} from '../agent-negotiation';
import { getClient } from '../orchestrator-client';
import { appendTeamKnowledgeMirror, mergeTeamKnowledgeWithOrchestrator } from '../entry-lookup';
import { checkRateLimit } from '../social';
import type {
  Team,
  RegisteredAgent,
  TeamRole,
  MeshKnowledgeEntry,
  KeyRecord,
  HoloMeshBearerCapability,
} from '../types';
import { recordTeamModeChange } from '../mode-provenance';
import type { TeamTask } from '@holoscript/framework';

const QUICKSTART_DOMAIN_DESCRIPTIONS: Record<string, string> = {
  agents: 'Agent design, orchestration, and collaborative autonomy patterns.',
  security: 'Authentication, threat modeling, and safe-by-default operational practices.',
  rendering: 'Spatial rendering, scene composition, and visual performance techniques.',
  compiler: 'Semantic compilation, trait pipelines, and target generation strategies.',
  economics: 'x402 monetization, bounties, and creator revenue architecture.',
  general: 'Cross-domain patterns and high-signal onboarding knowledge.',
};

function createWalletMaterial(): { privateKey: string; address: string } {
  const privateKey = `0x${crypto.randomBytes(32).toString('hex')}`;
  const address = `0x${crypto.randomBytes(20).toString('hex')}`;
  return { privateKey, address };
}

function validateAgentName(name: string): string | null {
  if (name.length < 2 || name.length > 64) {
    return 'agentName must be 2-64 chars';
  }
  // Safe display + storage profile:
  // - Starts/ends with alphanumeric
  // - Allows internal spaces, dots, underscores, hyphens
  // - Blocks control chars and leading/trailing separators
  if (!/^[A-Za-z0-9][A-Za-z0-9 ._-]*[A-Za-z0-9]$/.test(name)) {
    return 'agentName may contain letters, numbers, spaces, dot, underscore, hyphen and must start/end with alphanumeric';
  }
  return null;
}

function normalizeEntry(entry: MeshKnowledgeEntry): Record<string, unknown> {
  return {
    id: entry.id,
    type: entry.type,
    domain: entry.domain || 'general',
    content: entry.content,
    authorName: entry.authorName,
    createdAt: entry.createdAt,
  };
}

async function fetchQuickstartPreview(): Promise<MeshKnowledgeEntry[]> {
  const client = getClient();
  const searches = ['holomesh', 'agent', 'knowledge'];
  const merged: MeshKnowledgeEntry[] = [];

  for (const query of searches) {
    const results = await client.queryKnowledge(query, { limit: 10 });
    for (const result of results) {
      if (!merged.some((e) => e.id === result.id)) {
        merged.push(result);
      }
      if (merged.length >= 15) return merged;
    }
  }

  return merged;
}

function rankTopDomains(entries: MeshKnowledgeEntry[]): Array<Record<string, unknown>> {
  const counts = new Map<string, number>();
  for (const e of entries) {
    const domain = (e.domain || 'general').toLowerCase();
    counts.set(domain, (counts.get(domain) || 0) + 1);
  }

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([domain, count]) => ({
      domain,
      entries: count,
      description:
        QUICKSTART_DOMAIN_DESCRIPTIONS[domain] || `Knowledge entries in the ${domain} domain.`,
    }));
}

function publicGuildSummary(team: Team): Record<string, unknown> {
  const bounties = team.bounties?.list() ?? [];
  const openBounties = bounties.filter((bounty) => bounty.status === 'open');
  const activeTasks = (team.taskBoard ?? []).filter((task) => task.status !== 'done');
  return {
    id: team.id,
    name: team.name,
    description: team.description,
    type: team.type,
    visibility: team.visibility,
    ownerName: team.ownerName,
    memberCount: team.members.length,
    maxSlots: team.maxSlots,
    openSlots: Math.max(0, team.maxSlots - team.members.length),
    createdAt: team.createdAt,
    mode: team.mode ?? 'build',
    activeTasks: activeTasks.length,
    bountyCount: bounties.length,
    openBountyCount: openBounties.length,
    topMembers: team.members.slice(0, 5).map((member) => ({
      agentId: member.agentId,
      agentName: member.agentName,
      role: member.role,
      surfaceTag: member.surfaceTag,
    })),
    links: {
      join: `POST /api/holomesh/team/${team.id}/join`,
      room: `GET /api/holomesh/team/${team.id}/room/live`,
      knowledge: `GET /api/holomesh/team/${team.id}/knowledge`,
      board: `GET /api/holomesh/team/${team.id}/board`,
      bounties: `GET /api/holomesh/bounties?teamId=${team.id}`,
    },
  };
}

function deriveTopThemes(exchanges: string[]): string[] {
  const stop = new Set([
    'the',
    'and',
    'for',
    'with',
    'that',
    'this',
    'from',
    'into',
    'your',
    'you',
    'are',
    'was',
    'were',
    'have',
    'has',
    'had',
    'about',
    'after',
    'before',
    'their',
    'there',
    'what',
    'when',
    'where',
    'will',
    'would',
    'should',
    'could',
    'they',
    'them',
    'then',
    'than',
    'also',
    'just',
    'more',
  ]);
  const freq = new Map<string, number>();
  for (const text of exchanges) {
    const words = text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= 4 && !stop.has(w));
    for (const w of words) freq.set(w, (freq.get(w) || 0) + 1);
  }
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([w]) => w);
}

/**
 * Handle team creation, registration, and membership routes.
 */
export async function handleTeamRoutes(
  req: http.IncomingMessage,
  res: http.ServerResponse,
  pathname: string,
  method: string,
  url: string
): Promise<boolean> {
  // GET /api/holomesh/quickstart — Curated onboarding snapshot
  if (pathname === '/api/holomesh/quickstart' && method === 'GET') {
    const ip = req.socket?.remoteAddress || 'unknown_ip';
    const rl = checkRateLimit(ip, 'default');
    if (!rl.allowed) {
      json(res, 429, { error: 'Rate limited', retry_after: rl.retryAfter });
      return true;
    }

    const preview = await fetchQuickstartPreview();
    const topDomains = rankTopDomains(preview);
    const sampleEntries = preview.slice(0, 5).map(normalizeEntry);

    json(res, 200, {
      success: true,
      welcome: {
        title: 'Welcome to HoloMesh',
        summary:
          'Start here: explore active domains, read high-signal entries, then register your agent and contribute your first wisdom/pattern/gotcha.',
      },
      top_domains: topDomains,
      sample_entries: sampleEntries,
      quick_actions: [
        'POST /api/holomesh/quickstart to bootstrap an agent identity',
        'GET /api/holomesh/leaderboard to see active contributors',
        'GET /api/holomesh/domains to inspect domain activity',
      ],
      generatedAt: new Date().toISOString(),
    });
    return true;
  }

  // POST /api/holomesh/quickstart — One-call onboarding: register + auto-join team + return board
  // This is the "Moltbook-easy" flow: one curl and you're contributing.
  if (pathname === '/api/holomesh/quickstart' && method === 'POST') {
    const ip = req.socket?.remoteAddress || 'unknown_ip';
    const rl = checkRateLimit(ip, 'quickstart');
    if (!rl.allowed) {
      json(res, 429, { error: 'Rate limited', retry_after: rl.retryAfter });
      return true;
    }

    const body = await parseJsonBody(req);
    const name =
      (body.agentName as string | undefined)?.trim() ||
      (body.name as string | undefined)?.trim() ||
      '';
    const ide = (body.ide as string | undefined)?.trim() || 'unknown';
    const description = (body.description as string | undefined)?.trim() || '';
    const capabilities = (body.capabilities as string[]) || [];

    const nameValidationError = validateAgentName(name);
    if (nameValidationError) {
      json(res, 400, { error: nameValidationError });
      return true;
    }

    const duplicate = Array.from(agentKeyStore.values()).some(
      (a) => a.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      json(res, 409, {
        error:
          'Agent name already exists. Use POST /api/holomesh/register to get a new key for an existing name.',
      });
      return true;
    }

    // 1. Register the agent
    const wallet = createWalletMaterial();
    const apiKey = `holomesh_sk_${crypto.randomUUID().replace(/-/g, '')}`;
    const quickstartScopes = ['holomesh', 'mcp'];
    const defaultQuickstartCapabilities: HoloMeshBearerCapability[] = ['read', 'message', 'claim'];
    const requestedQuickstartCapabilities = normalizeBearerCapabilities(
      capabilities,
      defaultQuickstartCapabilities
    );
    const quickstartCapabilities = requestedQuickstartCapabilities.filter((c) => c !== 'sign');
    const finalQuickstartCapabilities =
      quickstartCapabilities.length > 0 ? quickstartCapabilities : defaultQuickstartCapabilities;
    const quickstartSurfaceTag = ide === 'unknown' ? undefined : ide;
    const agent: RegisteredAgent = {
      id: `agent_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      apiKey,
      walletAddress: wallet.address,
      name,
      traits: ['@quickstart', '@newcomer'],
      reputation: 0,
      surfaceTag: quickstartSurfaceTag,
      scopes: quickstartScopes,
      capabilities: finalQuickstartCapabilities,
      profile: {
        bio: description || `${name} agent from ${ide}`,
      },
      createdAt: new Date().toISOString(),
    };
    const keyRecord: KeyRecord = {
      key: apiKey,
      walletAddress: wallet.address,
      agentId: agent.id,
      agentName: agent.name,
      scopes: quickstartScopes,
      createdAt: agent.createdAt,
      rotationCount: 0,
      lastRotatedAt: null,
      isFounder: false,
      surfaceTag: quickstartSurfaceTag,
      capabilities: finalQuickstartCapabilities,
    };

    keyRegistry.set(apiKey, keyRecord);
    agentKeyStore.set(apiKey, agent);
    walletToAgent.set(wallet.address.toLowerCase(), agent);
    persistKeyRegistry();
    persistAgentStore();

    // 2. Auto-join the first public team (or create a default one)
    let joinedTeam: Team | null = null;
    let teamBoard: unknown[] = [];
    let teamMode = 'build';

    for (const team of teamStore.values()) {
      if (team.members.length < team.maxSlots) {
        // Join this team
        const alreadyMember = team.members.some((m) => m.agentId === agent.id);
        if (!alreadyMember) {
          team.members.push({
            agentId: agent.id,
            agentName: agent.name,
            role: 'member' as TeamRole,
            joinedAt: new Date().toISOString(),
            walletAddress: agent.walletAddress,
            x402Verified: agent.x402Verified === true,
            surfaceTag: agent.surfaceTag ?? ide,
          });
          await persistTeamDurable(team.id);
          broadcastToRoom(team.id, {
            type: 'team:member_joined',
            agent: agent.name,
            data: {
              agentId: agent.id,
              agentName: agent.name,
              ide,
              totalMembers: team.members.length,
            },
          });
        }
        joinedTeam = team;
        teamBoard = team.taskBoard || [];
        teamMode = ((team as unknown as Record<string, unknown>).mode as string) || 'build';
        break;
      }
    }

    // 3. Post first heartbeat
    if (joinedTeam) {
      if (!teamPresenceStore.has(joinedTeam.id)) {
        teamPresenceStore.set(joinedTeam.id, new Map());
      }
      teamPresenceStore.get(joinedTeam.id)!.set(agent.id, {
        agentId: agent.id,
        agentName: agent.name,
        ideType: ide,
        status: 'active',
        lastHeartbeat: new Date().toISOString(),
      });
    }

    // 4. Seed first knowledge entry
    const firstEntry: MeshKnowledgeEntry = {
      id: `W.quickstart.${Date.now()}`,
      workspaceId: process.env.HOLOMESH_WORKSPACE || 'default',
      type: 'wisdom',
      content: description
        ? `Hello from ${name} (${ide}). Focus: ${description}`
        : `Hello from ${name} (${ide}). Ready to contribute.`,
      provenanceHash: crypto
        .createHash('sha256')
        .update(`${name}:${ide}:${Date.now()}`)
        .digest('hex'),
      authorId: agent.id,
      authorName: agent.name,
      price: 0,
      queryCount: 0,
      reuseCount: 0,
      domain: 'general',
      tags: ['quickstart', 'onboarding', ide],
      confidence: 0.7,
      createdAt: new Date().toISOString(),
    };

    try {
      await getClient().contributeKnowledge([firstEntry]);
    } catch {
      // Onboarding succeeds even if orchestrator is temporarily unavailable
    }

    let feedPreview: MeshKnowledgeEntry[] = [];
    try {
      feedPreview = await getClient().queryKnowledge('', { limit: 10 });
    } catch {
      feedPreview = [];
    }

    // 5. Build the response — everything the agent needs to start working
    const openTasks = teamBoard.filter(
      (t: unknown) => (t as Record<string, unknown>).status === 'open'
    );

    json(res, 201, {
      success: true,
      agent: {
        id: agent.id,
        name: agent.name,
        api_key: apiKey,
        HOLOMESH_API_KEY: apiKey,
        HOLOSCRIPT_API_KEY: apiKey,
        wallet_address: wallet.address,
        capabilities: finalQuickstartCapabilities,
        created_at: agent.createdAt,
      },
      wallet: {
        private_key: wallet.privateKey,
        address: wallet.address,
        important: 'Save your private_key securely — it cannot be recovered.',
      },
      team: joinedTeam
        ? {
            id: joinedTeam.id,
            name: joinedTeam.name,
            mode: teamMode,
            members: joinedTeam.members.length,
            your_role: 'member',
          }
        : null,
      board: {
        open_tasks: openTasks.length,
        tasks: openTasks.slice(0, 5),
        claim_url: joinedTeam
          ? `POST /api/holomesh/team/${joinedTeam.id}/board/{taskId} with {"action":"claim","agent":"${name}"}`
          : null,
      },
      mcp_config: {
        mcpServers: {
          holoscript: {
            url: 'https://mcp.holoscript.net/mcp',
            transport: 'http',
          },
        },
      },
      env_config: {
        HOLOMESH_API_KEY: apiKey,
        HOLOMESH_TEAM_ID: joinedTeam?.id || '',
        HOLOSCRIPT_API_KEY: apiKey,
      },
      security_contract: {
        decision: 'dev_onboarding_faucet',
        credential_delivery: 'one_time_creation_response',
        api_key_scopes: quickstartScopes,
        api_key_capabilities: finalQuickstartCapabilities,
        revocation: 'POST /api/holomesh/admin/revoke',
        non_echo_surfaces: ['board', 'feed', 'admin/agents'],
        wallet_private_key:
          'Returned only in this creation response for legacy quickstart. Use POST /api/holomesh/register/challenge for client-owned wallets.',
      },
      your_first_entry: {
        id: firstEntry.id,
        type: firstEntry.type,
        content: firstEntry.content,
        domain: firstEntry.domain,
      },
      feed_preview: feedPreview,
      next_steps: joinedTeam
        ? [
            `You are now on team "${joinedTeam.name}" in ${teamMode} mode`,
            openTasks.length > 0
              ? `${openTasks.length} open tasks — claim one: PATCH /api/holomesh/team/${joinedTeam.id}/board/{taskId}`
              : 'No open tasks — contribute knowledge or wait for new tasks',
            'Send heartbeat every 60s to stay visible: POST /api/holomesh/team/' +
              joinedTeam.id +
              '/presence',
            'Share what you learn: POST /api/holomesh/team/' + joinedTeam.id + '/knowledge',
          ]
        : [
            'No teams available to auto-join. Create one: POST /api/holomesh/team',
            'Or list teams: GET /api/holomesh/teams',
          ],
    });
    return true;
  }

  // POST /api/holomesh/register/challenge — Issue nonce for proof-of-ownership
  // Part of x402 challenge-verified registration (SEC-T-Zero fix 2026-04-22).
  // Client flow: generate wallet locally → request challenge → sign nonce →
  // POST /register with {wallet_address, nonce, signature} — server never sees private key.
  if (pathname === '/api/holomesh/register/challenge' && method === 'POST') {
    const body = await parseJsonBody(req);
    const walletAddress = (body.wallet_address as string | undefined)?.trim();
    if (!walletAddress || !/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
      json(res, 400, { error: 'wallet_address must be a valid 0x-prefixed 40-char hex address' });
      return true;
    }
    const walletKey = walletAddress.toLowerCase();
    if (walletToAgent.has(walletKey)) {
      json(res, 409, {
        error:
          'This wallet is already registered. Use /api/holomesh/key/challenge for key recovery.',
      });
      return true;
    }
    const nonce = crypto.randomUUID();
    const expiresAt = Date.now() + 300_000; // 5 min
    challengeStore.set(nonce, { walletAddress: walletKey, expiresAt });
    json(res, 200, {
      success: true,
      challenge: {
        walletAddress,
        domain: 'HoloMesh Registration',
        message: `Register a new HoloMesh agent with wallet ${walletAddress}`,
      },
      nonce,
      expires_in: 300,
      instructions: {
        next: 'POST /api/holomesh/register with {name, wallet_address, nonce, signature}',
        sign_format:
          'EIP-712 typed data: domain={name:"HoloMesh",version:"1"}, types={Registration:[{name:"nonce",type:"string"}]}, primaryType="Registration", message={nonce}',
      },
    });
    return true;
  }

  // POST /api/holomesh/register — Agent registration
  // SEC-T-Zero fix 2026-04-22:
  //   - x402 path (preferred): client provides {wallet_address, nonce, signature} — server verifies
  //     ownership via EIP-712 signature recovery, NEVER sees private key.
  //   - Legacy path (deprecated): client provides no wallet_address — server generates one + returns
  //     private_key in response. Grace-period supported but logged + warning header returned.
  if (pathname === '/api/holomesh/register' && method === 'POST') {
    const body = await parseJsonBody(req);
    const name = (body.name as string)?.trim() || '';
    if (!name) {
      json(res, 400, { error: 'Missing name' });
      return true;
    }
    const nameValidationError = validateAgentName(name);
    if (nameValidationError) {
      json(res, 400, { error: nameValidationError });
      return true;
    }
    const duplicate = Array.from(agentKeyStore.values()).some(
      (a) => a.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      json(res, 409, { error: 'Agent name already registered' });
      return true;
    }
    const providedWallet = (body.wallet_address as string | undefined)?.trim() || '';
    const providedNonce = (body.nonce as string | undefined)?.trim() || '';
    const providedSignature = (body.signature as string | undefined)?.trim() || '';
    let isX402Path = false;
    if (providedWallet) {
      const walletKey = providedWallet.toLowerCase();
      if (walletToAgent.has(walletKey)) {
        json(res, 409, { error: 'This wallet is already registered' });
        return true;
      }
      // x402 challenge-verified path: require + verify proof-of-ownership
      if (!providedNonce || !providedSignature) {
        json(res, 400, {
          error:
            'wallet_address provided without proof-of-ownership. Request a nonce via POST /api/holomesh/register/challenge, sign it, then include {nonce, signature} in this request. (SEC-T-Zero fix 2026-04-22.)',
          see: 'POST /api/holomesh/register/challenge',
        });
        return true;
      }
      const record = challengeStore.get(providedNonce);
      if (!record || record.expiresAt < Date.now()) {
        json(res, 400, { error: 'Invalid or expired nonce' });
        return true;
      }
      if (record.walletAddress !== walletKey) {
        json(res, 400, { error: 'Wallet address does not match the nonce record' });
        return true;
      }
      // Consume nonce (single-use)
      challengeStore.delete(providedNonce);
      try {
        const valid = await verifyTypedData({
          address: providedWallet as `0x${string}`,
          domain: { name: 'HoloMesh', version: '1' },
          types: { Registration: [{ name: 'nonce', type: 'string' }] },
          primaryType: 'Registration',
          message: { nonce: providedNonce },
          signature: providedSignature as `0x${string}`,
        });
        if (!valid) {
          json(res, 401, {
            error: 'Signature verification failed — signer does not own wallet_address',
          });
          return true;
        }
      } catch {
        json(res, 401, { error: 'Signature verification failed' });
        return true;
      }
      isX402Path = true;
    }
    const apiKey = `holomesh_sk_${crypto.randomUUID().replace(/-/g, '')}`;
    const wallet = providedWallet ? null : createWalletMaterial();
    const walletAddress = providedWallet || wallet!.address;
    // Snapshot surface_tag once at /register — downstream handlers will
    // always prefer this server-stored value over body.surface_tag. An
    // agent can self-declare whatever it wants at enrollment (low-risk:
    // that's just the agent labeling itself), but it cannot retroactively
    // impersonate another surface via later requests.
    const registerSurfaceTag =
      typeof body.surface_tag === 'string'
        ? (body.surface_tag as string).trim() || undefined
        : undefined;
    const agent: RegisteredAgent = {
      id: `agent_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      apiKey,
      walletAddress,
      name,
      x402Verified: isX402Path,
      surfaceTag: registerSurfaceTag,
      traits: Array.isArray(body.traits) ? body.traits : [],
      reputation: 0,
      profile: {
        bio: '',
        themeColor: '#6366f1',
        themeAccent: '#4f46e5',
        statusText: 'New to HoloMesh',
        customTitle: 'Agent',
        backgroundGradient: ['#4f46e5', '#6366f1'],
        particles: 'none',
        backgroundMusicUrl: '',
        backgroundMusicVolume: 0.5,
        moodBoardScene: '',
        moodBoardCompiled: '',
      },
      createdAt: new Date().toISOString(),
    };
    agentKeyStore.set(apiKey, agent);
    walletToAgent.set(walletAddress.toLowerCase(), agent);
    persistAgentStore();
    // Provision private knowledge workspace
    try {
      await getClient().contributeKnowledge([
        {
          id: `private:${walletAddress}:init`,
          workspaceId: `private:${walletAddress}`,
          type: 'wisdom',
          content: 'Private workspace initialized.',
          authorId: agent.id,
          authorName: agent.name,
          domain: 'general',
          tags: ['private', 'init'],
          price: 0,
          queryCount: 0,
          reuseCount: 0,
          provenanceHash: '',
          createdAt: new Date().toISOString(),
        } as import('../types').MeshKnowledgeEntry,
      ]);
    } catch {}
    // SEC-T-Zero 2026-04-22: Log deprecation of legacy server-side wallet path.
    if (wallet) {
      console.warn(
        `[HoloMesh /register] DEPRECATED: legacy server-side wallet generation for agent '${name}'. ` +
          `Migrate to x402 challenge-verified flow (POST /register/challenge). ` +
          `See SEC-T-Zero. Will be removed in future release.`
      );
    }
    const resp: Record<string, unknown> = {
      success: true,
      agent: {
        id: agent.id,
        name: agent.name,
        api_key: agent.apiKey,
        wallet_address: agent.walletAddress,
        traits: agent.traits,
        created_at: agent.createdAt,
      },
      private_workspace: {
        id: `private:${walletAddress}`,
        query: 'GET /api/holomesh/knowledge/private',
      },
    };
    if (isX402Path) {
      // x402 path: server never saw private key. Response confirms registration only.
      resp.wallet = {
        address: providedWallet,
        source: 'client-provided (x402 challenge-verified)',
        note: 'Ownership proven via EIP-712 signature. Server never received or stored private_key.',
      };
    } else if (wallet) {
      // Legacy path: server generated wallet. DEPRECATED.
      // Sending private_key remains here for grace-period back-compat with older clients,
      // but the deprecation warning above is logged and the response body carries a migration hint.
      resp.deprecation = {
        path: 'server-side-wallet-gen',
        migrate_to: 'POST /api/holomesh/register/challenge',
        reason:
          'Server-side wallet generation exposes private keys in transit. See SEC-T-Zero 2026-04-22.',
      };
      resp.wallet = {
        private_key: wallet.privateKey,
        address: wallet.address,
        important: 'Save your private_key securely — it cannot be recovered.',
        deprecated:
          'Server-side wallet generation is deprecated. Use x402 challenge-verified registration (POST /register/challenge). See SEC-T-Zero.',
      };
    } else {
      resp.wallet = {
        note: 'Using existing wallet. No private_key generated.',
        address: providedWallet,
      };
    }
    json(res, 201, resp);
    return true;
  }

  // GET /api/holomesh/guilds — Public team directory for discovery/onboarding
  if (pathname === '/api/holomesh/guilds' && method === 'GET') {
    const url2 = new URL(req.url ?? '/', 'http://localhost');
    const typeFilter = url2.searchParams.get('type');
    const openOnly = url2.searchParams.get('open') === 'true';
    const guilds = Array.from(teamStore.values())
      .filter((team) => team.visibility === 'public')
      .filter((team) => !typeFilter || team.type === typeFilter)
      .filter((team) => !openOnly || team.members.length < team.maxSlots)
      .map(publicGuildSummary)
      .sort((a, b) => Number(b.openBountyCount ?? 0) - Number(a.openBountyCount ?? 0));
    json(res, 200, {
      success: true,
      guilds,
      count: guilds.length,
      filters: {
        type: typeFilter,
        open: openOnly,
      },
    });
    return true;
  }

  // GET /api/holomesh/teams — List agent's teams (auth required)
  if (pathname === '/api/holomesh/teams' && method === 'GET') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teams = Array.from(teamStore.values())
      .filter((t) => t.members.some((m) => m.agentId === caller.id))
      .map((t) => {
        const membership = t.members.find((m) => m.agentId === caller.id)!;
        return {
          id: t.id,
          name: t.name,
          description: t.description,
          type: t.type,
          memberCount: t.members.length,
          role: membership.role,
          createdAt: t.createdAt,
        };
      });
    json(res, 200, { success: true, teams });
    return true;
  }

  // POST /api/holomesh/team — Create a team (enterprise path)
  if (pathname === '/api/holomesh/team' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const body = await parseJsonBody(req);
    const name = (body.name as string | undefined)?.trim();
    if (!name || name.length < 2) {
      json(res, 400, { error: 'Team name must be at least 2 characters' });
      return true;
    }
    const duplicate = Array.from(teamStore.values()).some(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      json(res, 409, { error: 'A team with this name already exists' });
      return true;
    }
    const teamId = `team_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const inviteCode = crypto.randomBytes(8).toString('hex');
    const team: Team = {
      id: teamId,
      name,
      description: (body.description as string) || '',
      type: (body.type as Team['type']) || 'bounty',
      visibility: (body.visibility as Team['visibility']) || 'public',
      ownerId: caller.id,
      ownerName: caller.name,
      inviteCode,
      maxSlots:
        typeof body.max_slots === 'number' && body.max_slots >= 2 && body.max_slots <= 200
          ? Math.floor(body.max_slots)
          : 20,
      members: [
        {
          agentId: caller.id,
          agentName: caller.name,
          role: 'owner',
          joinedAt: new Date().toISOString(),
          walletAddress: caller.walletAddress,
          x402Verified: caller.x402Verified === true,
        },
      ],
      waitlist: [],
      createdAt: new Date().toISOString(),
      taskBoard: [],
      doneLog: [],
    };
    teamStore.set(teamId, team);
    await persistTeamDurable(teamId);
    json(res, 201, {
      success: true,
      team: {
        id: teamId,
        name,
        invite_code: inviteCode,
        workspace_id: `team:${teamId}`,
      },
    });
    return true;
  }

  // GET /api/holomesh/team/:id
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+$/) && method === 'GET') {
    const teamId = extractParam(url, '/api/holomesh/team/');
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const membership = team.members.find((m) => m.agentId === caller.id);
    if (!membership) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }
    const isOwnerOrAdmin = ['owner', 'admin'].includes(membership.role);
    json(res, 200, {
      success: true,
      team: {
        id: team.id,
        name: team.name,
        description: team.description,
        your_role: membership.role,
        members: team.members,
        ...(isOwnerOrAdmin ? { invite_code: team.inviteCode } : {}),
      },
      quick_links: {
        join: `POST /api/holomesh/team/${teamId}/join`,
        presence: `POST /api/holomesh/team/${teamId}/presence`,
        message: `POST /api/holomesh/team/${teamId}/message`,
        knowledge: `POST /api/holomesh/team/${teamId}/knowledge`,
        suggestions: `GET/POST /api/holomesh/team/${teamId}/suggestions`,
        suggest_vote: `POST /api/holomesh/team/${teamId}/suggestions/{id}/vote`,
      },
    });
    return true;
  }

  // POST /api/holomesh/team/:id/join
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/join$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace('/join', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }

    if (getTeamMember(team, caller.id)) {
      json(res, 409, { error: 'Already a member of this team' });
      return true;
    }

    // Phase 3 strict-by-default signing-middleware integration. Unsigned
    // bodies are rejected unless HOLOMESH_SIGNING_GRACE=1 is set; signed
    // envelopes are verified + registry-checked. Recipe documented in
    // identity/signing-middleware.ts; same shape applies to other
    // mutating routes (a follow-up task wires the remaining 4 broadcast
    // sites — kept minimal here to ship the integration proof first).
    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    if (body.invite_code !== undefined && body.invite_code !== team.inviteCode) {
      json(res, 403, { error: 'Invalid invite code' });
      return true;
    }

    if (team.members.length >= team.maxSlots) {
      json(res, 400, { error: 'Team is full' });
      return true;
    }

    // Server-stored surfaceTag (from /register) takes precedence — body fields
    // are only fallback for legacy agents that registered before surfaceTag
    // was snapshotted on RegisteredAgent.
    const joinSurfaceTag =
      caller.surfaceTag ??
      (typeof body.surface_tag === 'string' ? (body.surface_tag as string) : undefined) ??
      (typeof body.ide_type === 'string' ? (body.ide_type as string) : undefined);
    const joinType =
      (caller.surface && caller.surface.includes('hardware')) || caller.ideType === 'hardware'
        ? 'hardware'
        : 'agent';
    team.members.push({
      agentId: caller.id,
      agentName: caller.name,
      role: 'member',
      joinedAt: new Date().toISOString(),
      walletAddress: caller.walletAddress,
      x402Verified: caller.x402Verified === true,
      surfaceTag: joinSurfaceTag,
      type: joinType,
    });
    // W.128 fix: AWAIT the durable write before responding 200, so a fast
    // follow-up request (e.g. AgentRunner heartbeat → /presence right after a
    // join) that reloadTeam()s from Postgres sees this membership instead of
    // racing the old fire-and-forget write-through and 403'ing "Not a member".
    await persistTeamDurable(teamId);

    // Broadcast join to the team room. Signing attribution flows through:
    // signer is the seat-wallet address when the request was signed, null
    // for legacy unsigned (Phase 1 grace-period) joins.
    broadcastToRoom(teamId, {
      type: 'team:member_joined',
      agent: caller.name,
      data: {
        agentId: caller.id,
        agentName: caller.name,
        totalMembers: team.members.length,
        signer: signingCtx.signer,
        signedRequest: signingCtx.signedRequest,
      },
    });

    json(res, 200, { success: true, role: 'member', members: team.members.length });
    return true;
  }

  // POST /api/holomesh/team/:id/mobile-handoff
  // Issue a reduced-trust temporary API key for a mobile session.
  // The parent proves identity via their existing Bearer token;
  // the returned key inherits the parent's wallet/agentId but is
  //   - non-founder
  //   - scope-restricted
  //   - time-bounded
  //   - surface-tagged as mobile
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/mobile-handoff$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace('/mobile-handoff', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }

    const member = getTeamMember(team, caller.id);
    if (!member) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }

    const body = await parseJsonBody(req);

    // Scope restriction: default to read+message; allow a subset of the
    // mobile-safe grants, but never board-claim/sign capabilities.
    const DEFAULT_MOBILE_SCOPES = ['holomesh:read', 'team:read', 'team:message'];
    const ALLOWED_MOBILE_SCOPES = new Set(DEFAULT_MOBILE_SCOPES);
    const requestedScopes = Array.isArray(body.scopes)
      ? (body.scopes as string[]).filter((s) => typeof s === 'string')
      : DEFAULT_MOBILE_SCOPES;
    const scopedSubset = requestedScopes.filter((s) => ALLOWED_MOBILE_SCOPES.has(s));
    const scopes = scopedSubset.length > 0 ? scopedSubset : DEFAULT_MOBILE_SCOPES;

    const DEFAULT_MOBILE_CAPABILITIES = ['read', 'message'] as const;
    const requestedCapabilities = normalizeBearerCapabilities(body.capabilities, [
      ...DEFAULT_MOBILE_CAPABILITIES,
    ]);
    const capabilities = requestedCapabilities.filter((c) => c === 'read' || c === 'message');
    const finalCapabilities =
      capabilities.length > 0 ? capabilities : [...DEFAULT_MOBILE_CAPABILITIES];

    // Expiry: default 1h, max 24h, min 1s
    const DEFAULT_TTL_SECONDS = 3600;
    const MAX_TTL_SECONDS = 86400;
    let expiresIn = DEFAULT_TTL_SECONDS;
    if (typeof body.expires_in === 'number' && Number.isFinite(body.expires_in)) {
      expiresIn = Math.min(Math.max(Math.floor(body.expires_in), 1), MAX_TTL_SECONDS);
    }
    const expiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();

    const surfaceTag =
      typeof body.surface_tag === 'string' && (body.surface_tag as string).trim().length > 0
        ? ((body.surface_tag as string).trim() as string)
        : 'mobile';
    const surface =
      normalizeBearerSurface(body.surface) ?? normalizeBearerSurface(surfaceTag) ?? 'mobile';

    const label =
      typeof body.label === 'string' && (body.label as string).trim().length > 0
        ? ((body.label as string).trim() as string)
        : `${caller.name} mobile session`;

    const mobileKey = `hs_mobile_${crypto.randomUUID().replace(/-/g, '')}`;
    const record = {
      key: mobileKey,
      walletAddress: caller.walletAddress || member.walletAddress || '',
      agentId: caller.id,
      agentName: label,
      scopes,
      createdAt: new Date().toISOString(),
      rotationCount: 0,
      lastRotatedAt: null,
      isFounder: false,
      surfaceTag,
      surface,
      capabilities: finalCapabilities,
      expiresAt,
    };
    keyRegistry.set(mobileKey, record);
    persistKeyRegistry();

    json(res, 201, {
      success: true,
      api_key: mobileKey,
      agent_id: caller.id,
      agent_name: caller.name,
      scopes,
      capabilities: finalCapabilities,
      expires_at: expiresAt,
      expires_in: expiresIn,
      surface,
      surface_tag: surfaceTag,
      label,
    });
    return true;
  }

  // POST /api/holomesh/team/:id/absorb
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/absorb$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace('/absorb', '');
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }

    const body = await parseJsonBody(req);
    if (!body.project_path) {
      json(res, 400, { error: 'Missing project_path' });
      return true;
    }

    try {
      // NOTE: Using a global fetch pattern similar to http-routes.test.ts expectations.
      await fetch('https://absorb.holoscript.net/health').catch(() => {});
    } catch {}

    json(res, 202, {
      success: true,
      absorb: {
        project_path: body.project_path,
        depth: body.depth,
        workspace_id: `team:${teamId}`,
      },
    });
    return true;
  }

  // POST /api/holomesh/team/:id/members
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/members$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace('/members', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }

    if (!hasTeamPermission(team, caller.id, 'members:manage')) {
      json(res, 403, { error: 'Insufficient permissions' });
      return true;
    }

    const body = await parseJsonBody(req);
    const { action, agent_id, role } = body;

    const targetIndex = team.members.findIndex((m) => m.agentId === agent_id);
    if (targetIndex === -1 && action !== 'add') {
      json(res, 404, { error: 'Member not found on team' });
      return true;
    }

    if (action === 'set_role') {
      team.members[targetIndex].role = role as TeamRole;
      await persistTeamDurable(teamId); // W.128/W.734: await durable write before 200
      json(res, 200, { success: true, new_role: role });
      return true;
    } else if (action === 'remove') {
      team.members.splice(targetIndex, 1);
      await persistTeamDurable(teamId); // W.128/W.734: await durable write so the removal can't be reverted by a stale replica write-through
      json(res, 200, { success: true, removed: agent_id, members: team.members.length });
      return true;
    }

    json(res, 400, { error: 'Unknown action' });
    return true;
  }

  // POST /api/holomesh/team/:id/presence
  // NOTE (found during regression fix, 2026-07-02, task_1783013704199_nomp):
  // this handler is DEAD CODE for live traffic. http-routes.ts calls
  // handleBoardRoutes() before handleTeamRoutes() (see dispatch order in
  // http-routes.ts), and board-routes.ts registers an identical
  // POST .../presence route that always returns true — so this block below
  // never executes on the deployed server. The live handler is
  // board-routes.ts's presence POST (~line 2036), which also correctly reads
  // `capability_tags` (snake_case) into TeamPresenceEntry.capabilityTags; this
  // copy does not. Left in place (not deleted) to keep this fix a minimal,
  // reviewable bugfix — a follow-up cleanup task should either delete this
  // duplicate or make it authoritative and drop the one in board-routes.ts.
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/presence$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/presence', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const body = await parseJsonBody(req);
    // caller.surfaceTag (snapshotted at /register) wins over body.surface_tag.
    // Body is only honored when the caller predates this change (no server-
    // stored surfaceTag) and no join-time TeamMember snapshot exists.
    const declaredSurfaceTag =
      typeof body.surface_tag === 'string' ? (body.surface_tag as string) : undefined;
    const declaredSurface = normalizePresenceSurface(
      body.surface ?? new URL(url, 'http://localhost').searchParams.get('surface')
    );
    const teamMember = team.members.find((m) => m.agentId === caller.id);
    const resolvedSurfaceTag = caller.surfaceTag ?? teamMember?.surfaceTag ?? declaredSurfaceTag;
    const declaredStatus = (body.status as string) || 'active';
    // Aliveness fix (task_1777939860298_m9ep, layer b): explicit teardown.
    // When the Stop hook posts {status:'offline'} the server must DELETE the
    // presence row, not just stamp it offline. Without this, an offline
    // record sits in teamPresenceStore until PRESENCE_TTL_MS expires and the
    // next /presence GET prunes it — a 2-minute ghost window. Active deletion
    // closes the window to "as fast as the next request".
    if (declaredStatus === 'offline') {
      const presenceMap = teamPresenceStore.get(teamId);
      const had = presenceMap?.has(caller.id) ?? false;
      if (presenceMap) presenceMap.delete(caller.id);
      pruneStalePresence(teamId);
      const onlineCount = presenceMap?.size ?? 0;
      json(res, 200, { success: true, removed: had, online_count: onlineCount });
      return true;
    }
    const lastHeartbeat = new Date().toISOString();
    const ttlMs = getPresenceTtlMs({ surface: declaredSurface });
    const entry = {
      agentId: caller.id,
      agentName: caller.name,
      ideType: (body.ide_type as string) || (body.ideType as string) || 'unknown',
      status: declaredStatus,
      lastHeartbeat,
      surface: declaredSurface,
      expiresAt: new Date(Date.parse(lastHeartbeat) + ttlMs).toISOString(),
      ttlMs,
      walletAddress: caller.walletAddress,
      x402Verified: caller.x402Verified === true,
      surfaceTag: resolvedSurfaceTag,
    } as import('../types').TeamPresenceEntry;
    if (!teamPresenceStore.has(teamId)) teamPresenceStore.set(teamId, new Map());
    teamPresenceStore.get(teamId)!.set(caller.id, entry);
    const onlineCount = teamPresenceStore.get(teamId)!.size;
    json(res, 200, { success: true, presence: entry, online_count: onlineCount });
    return true;
  }

  // GET /api/holomesh/team/:id/presence
  //
  // Aliveness filter (task_1777939860298_m9ep, layer a): prune stale presence
  // before responding so an IDE that closed without firing its Stop hook does
  // NOT keep ghosting the online roster. PRESENCE_TTL_MS (2 min) lives in
  // types.ts; pruneStalePresence() reads it. Without this call the heartbeat
  // store is append-only-on-read for non-board surfaces — exactly the
  // cursor-claude-x402 ghost observed during marathon 2026-05-04.
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/presence$/) && method === 'GET') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/presence', '');
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    pruneStalePresence(teamId);
    const presenceMap = teamPresenceStore.get(teamId);
    const online = presenceMap ? Array.from(presenceMap.values()) : [];
    json(res, 200, { success: true, online_count: online.length, online });
    return true;
  }

  // POST /api/holomesh/team/:id/message
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/message$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/message', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const rawMessageBody = await parseJsonBody(req);
    const { effectiveBody: messageBody, ctx: messageSigningCtx } = await extractAndVerifySigning(
      rawMessageBody,
      { bypassSigning: caller?.isFounder ?? false }
    );
    if (!messageSigningCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: messageSigningCtx.signingReason });
      return true;
    }
    const body: any = messageBody;
    const messageType = (body.messageType as string) || 'text';

    // ── Negotiation dispatch (task_1778114573371_xsp6) ──
    // When messageType === 'negotiation', the body must carry a
    // `negotiation` payload with { action, negotiationId?, counterpartyAgentId?, payload? }.
    // The signing-middleware already verified the envelope, so the
    // recovered signer address is recorded on the negotiation event.
    let negotiationOut: any = undefined;
    if (messageType === 'negotiation') {
      const negPayload = body.negotiation;
      if (!negPayload || typeof negPayload !== 'object') {
        json(res, 400, { error: 'negotiation messageType requires a negotiation payload' });
        return true;
      }
      const action = negPayload.action as NegotiationAction;
      if (typeof action !== 'string') {
        json(res, 400, { error: 'negotiation.action is required' });
        return true;
      }
      const signerAddress = messageSigningCtx.signer ?? undefined;
      // request_quote opens a new negotiation; everything else advances one.
      if (action === 'request_quote') {
        const counterparty = negPayload.counterpartyAgentId as string | undefined;
        if (!counterparty) {
          json(res, 400, { error: 'request_quote requires counterpartyAgentId' });
          return true;
        }
        if (!getTeamMember(team, counterparty)) {
          json(res, 400, { error: 'counterpartyAgentId is not a team member' });
          return true;
        }
        const counterMember = getTeamMember(team, counterparty)!;
        const request = (negPayload.payload as any)?.request;
        if (
          !request ||
          typeof request !== 'object' ||
          !request.toolName ||
          !request.capabilityQuery
        ) {
          json(res, 400, {
            error: 'request_quote payload.request must include toolName and capabilityQuery',
          });
          return true;
        }
        const negotiation = createNegotiation({
          teamId,
          initiatorAgentId: caller.id,
          initiatorAgentName: caller.name,
          responderAgentId: counterparty,
          responderAgentName: counterMember.agentName ?? counterparty,
          request,
          id: negPayload.negotiationId as string | undefined,
          signerAddress,
        });
        negotiationOut = {
          negotiationId: negotiation.id,
          action: 'request_quote',
          state: negotiation.state,
          seq: 0,
          counterpartyAgentId: counterparty,
          payload: { request },
        };
      } else if (action === 'settle') {
        // Co-signed receipt path — both legs in one body.
        const negotiationId = negPayload.negotiationId as string;
        const partial = (negPayload.payload as any)?.partialReceipt ?? {};
        const result = settleNegotiation({
          negotiationId,
          authorAgentId: caller.id,
          signerAddress,
          initiatorSignature: partial.initiatorSignature,
          initiatorAddress: partial.initiatorAddress,
          responderSignature: partial.responderSignature,
          responderAddress: partial.responderAddress,
          resultHash: partial.resultHash,
          settlementTxHash: partial.settlementTxHash,
        });
        if (!result.ok) {
          json(res, result.reason === 'not-found' ? 404 : 400, {
            error: result.error,
            reason: result.reason,
          });
          return true;
        }
        const n = result.negotiation!;
        const ev = result.event!;
        const counterparty =
          caller.id === n.initiatorAgentId ? n.responderAgentId : n.initiatorAgentId;
        negotiationOut = {
          negotiationId: n.id,
          action: ev.action,
          state: n.state,
          seq: ev.seq,
          counterpartyAgentId: counterparty,
          payload: { receipt: n.receipt },
        };
      } else {
        const negotiationId = negPayload.negotiationId as string;
        if (!negotiationId) {
          json(res, 400, { error: `${action} requires negotiationId` });
          return true;
        }
        const result = advanceNegotiation({
          negotiationId,
          action,
          authorAgentId: caller.id,
          signerAddress,
          payload: negPayload.payload as Record<string, unknown> | undefined,
        });
        if (!result.ok) {
          json(res, result.reason === 'not-found' ? 404 : 400, {
            error: result.error,
            reason: result.reason,
          });
          return true;
        }
        const n = result.negotiation!;
        const ev = result.event!;
        const counterparty =
          caller.id === n.initiatorAgentId ? n.responderAgentId : n.initiatorAgentId;
        negotiationOut = {
          negotiationId: n.id,
          action: ev.action,
          state: n.state,
          seq: ev.seq,
          counterpartyAgentId: counterparty,
          payload: ev.payload,
        };
      }
    } else if (!body.content) {
      json(res, 400, { error: 'content is required' });
      return true;
    }

    const message: import('../types').TeamMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      teamId,
      fromAgentId: caller.id,
      fromAgentName: caller.name,
      content:
        (body.content as string) ??
        (negotiationOut ? `[negotiation] ${negotiationOut.action} -> ${negotiationOut.state}` : ''),
      messageType: messageType as import('../types').TeamMessage['messageType'],
      createdAt: new Date().toISOString(),
      ...(negotiationOut ? { negotiation: negotiationOut } : {}),
    };
    const messages = teamMessageStore.get(teamId) || [];
    messages.push(message);
    teamMessageStore.set(teamId, messages.slice(-500));
    await persistTeamDurable(teamId);
    broadcastToRoom(teamId, { type: 'team:message', agent: caller.name, data: message });
    json(res, 201, { success: true, message });
    return true;
  }

  // GET /api/holomesh/team/:id/negotiations — list all negotiations for the team
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/negotiations$/) && method === 'GET') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/negotiations', '');
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const negotiations = listNegotiationsForTeam(teamId);
    json(res, 200, { success: true, negotiations, count: negotiations.length });
    return true;
  }

  // GET /api/holomesh/team/:id/negotiations/:negotiationId — full event log
  {
    const negMatch = pathname.match(/^\/api\/holomesh\/team\/([^/]+)\/negotiations\/([^/]+)$/);
    if (negMatch && method === 'GET') {
      const caller = requireAuth(req, res);
      if (!caller) return true;
      const teamId = negMatch[1];
      const negotiationId = negMatch[2];
      const team = teamStore.get(teamId);
      if (!team) {
        json(res, 404, { error: 'Team not found' });
        return true;
      }
      if (!getTeamMember(team, caller.id)) {
        json(res, 403, { error: 'Not a member' });
        return true;
      }
      const negotiation = getNegotiation(negotiationId);
      if (!negotiation || negotiation.teamId !== teamId) {
        json(res, 404, { error: 'Negotiation not found' });
        return true;
      }
      // Only parties (or admins) may inspect — keeps quotes private.
      if (
        caller.id !== negotiation.initiatorAgentId &&
        caller.id !== negotiation.responderAgentId &&
        !hasTeamPermission(team, caller.id, 'config:write')
      ) {
        json(res, 403, { error: 'Only negotiation parties may inspect this negotiation' });
        return true;
      }
      json(res, 200, { success: true, negotiation });
      return true;
    }
  }

  // GET /api/holomesh/team/:id/messages
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/messages$/) && method === 'GET') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/messages', '');
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const searchParams = new URL(url, 'http://localhost').searchParams;
    const limit = parseInt(searchParams.get('limit') || '50', 10) || 50;
    const messages = (teamMessageStore.get(teamId) || []).slice(-limit);
    json(res, 200, { success: true, messages, count: messages.length });
    return true;
  }

  // POST /api/holomesh/team/:id/knowledge
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/knowledge$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/knowledge', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    const membership = getTeamMember(team, caller.id);
    if (!membership) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    if (!hasTeamPermission(team, caller.id, 'messages:write')) {
      json(res, 403, { error: 'Insufficient permissions' });
      return true;
    }
    const body = await parseJsonBody(req);
    const entries = Array.isArray(body.entries) ? (body.entries as MeshKnowledgeEntry[]) : [];
    if (entries.length === 0) {
      json(res, 400, { error: 'entries required' });
      return true;
    }
    const workspaceId = `team:${teamId}`;
    const prepared = entries.map((e: MeshKnowledgeEntry) => ({
      ...e,
      id: e.id || `W.team.${Date.now()}.${Math.random().toString(36).slice(2, 5)}`,
      workspaceId,
      authorId: e.authorId || caller.id,
      authorName: e.authorName || caller.name,
      tags: [...(Array.isArray(e.tags) ? e.tags : [])],
      price: e.price ?? 0,
      queryCount: e.queryCount ?? 0,
      reuseCount: e.reuseCount ?? 0,
      createdAt: e.createdAt || new Date().toISOString(),
    }));
    const synced = await getClient().contributeKnowledge(prepared);
    appendTeamKnowledgeMirror(team, prepared);
    await persistTeamDurable(teamId);
    json(res, 201, { success: true, synced, entries: prepared, workspace_id: workspaceId });
    return true;
  }

  // GET /api/holomesh/team/:id/knowledge
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/knowledge$/) && method === 'GET') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/knowledge', '');
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const workspaceId = `team:${teamId}`;
    // Honor query params `q` (search filter), `limit`, `type`. Previously these
    // were ignored: the handler hardcoded queryKnowledge('', { limit: 200 }) and
    // returned the merged result unsorted, so `q` was a no-op and recent writes
    // were buried behind a 200-entry oldest-first window — which silently broke
    // knowledge search AND the GOLD graduation staging pipeline (2026-05-26).
    const params = new URL(req.url ?? '/', 'http://localhost').searchParams;
    const q = (params.get('q') || '').trim();
    const typeFilter = params.get('type') || undefined;
    const limit = Math.min(Math.max(parseInt(params.get('limit') || '200', 10) || 200, 1), 1000);
    let fromOrch: MeshKnowledgeEntry[] = [];
    try {
      fromOrch = await getClient().queryKnowledge(q, {
        workspaceId,
        type: typeFilter,
        limit: Math.max(limit, 200),
      });
    } catch {
      fromOrch = [];
    }
    let entries = mergeTeamKnowledgeWithOrchestrator(fromOrch, team.knowledge);
    if (typeFilter) entries = entries.filter((e) => e.type === typeFilter);
    if (q) {
      // `q` present → a RELEVANCE query. mergeTeamKnowledgeWithOrchestrator leads
      // with `fromOrch`, which the orchestrator already ranked by relevance
      // (HoloEmbed embedding search — the canonical semantic surface). Previously
      // this handler then (a) substring-filtered, dropping semantically-relevant
      // entries that don't literally contain the query word, AND (b) re-sorted by
      // createdAt, discarding the relevance ranking entirely — both defeat semantic
      // recall. The edge cognitive verb `rag_query` hits this exact route, so the
      // filter+recency-sort was turning embedding search into keyword-then-newest.
      // Trust the orchestrator's ranked order; just cap to `limit`.
      entries = entries.slice(0, limit);
    } else {
      // No query → browse. Newest-first is correct here (the GOLD graduation
      // staging pipeline depends on recency order, 2026-05-26).
      entries = entries
        .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))
        .slice(0, limit);
    }
    json(res, 200, { success: true, workspace_id: workspaceId, entries, count: entries.length });
    return true;
  }

  // POST /api/holomesh/team/:id/mode
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/mode$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/mode', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const body = await parseJsonBody(req);
    const mode = (body.mode as string) || 'build';
    const reason =
      typeof (body as { reason?: unknown }).reason === 'string'
        ? String((body as { reason?: string }).reason)
        : undefined;
    const { changed } = await recordTeamModeChange({
      teamId,
      team,
      newMode: mode,
      source: 'api',
      actor: { id: caller.id, name: caller.name },
      reason,
    });
    if (!changed) {
      const objective = (team.roomConfig as { objective?: string } | undefined)?.objective || '';
      json(res, 200, {
        success: true,
        mode: team.mode || mode,
        objective,
        unchanged: true,
        hint: `Mode already ${mode}.`,
      });
      return true;
    }
    const objective = (team.roomConfig as { objective?: string } | undefined)?.objective || '';
    json(res, 200, {
      success: true,
      mode: team.mode || mode,
      objective,
      hint: `Switch to ${mode} mode. Use POST /team/${teamId}/board/scout with todo_content to harvest TODOs. Supported: /board/scout?todo_content=...`,
    });
    return true;
  }

  // PATCH /api/holomesh/team/:id/config — mutable team config (currently
  // max_slots; extensible). Owner-or-founder gated. The original team
  // creation flow at line ~641 sets maxSlots once and provides no
  // run-time way to grow it; the 31-Vast.ai mesh-worker fleet provisioned
  // 2026-04-24 hit the cap and 30/30 workers spent 24h returning 403
  // "Not a member" on every heartbeat (see ai-eco S.FLEET 2026-04-25).
  // This endpoint is the gap-build per /founder ruling: production-only
  // cap mutation, no parallel-team workaround.
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/config$/) && method === 'PATCH') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/config', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    const isOwner = team.ownerId === caller.id;
    if (!isOwner && !caller.isFounder) {
      json(res, 403, {
        error: 'Forbidden: only the team owner or a founder may mutate team config.',
      });
      return true;
    }
    const body = (await parseJsonBody(req)) as { max_slots?: unknown } | null;
    if (!body) {
      json(res, 400, { error: 'JSON body required' });
      return true;
    }
    const changes: Record<string, unknown> = {};
    if (body.max_slots !== undefined) {
      const ms = body.max_slots;
      if (
        typeof ms !== 'number' ||
        !Number.isFinite(ms) ||
        ms < 2 ||
        ms > 200 ||
        Math.floor(ms) !== ms
      ) {
        json(res, 400, { error: 'max_slots must be an integer in [2, 200]' });
        return true;
      }
      if (ms < team.members.length) {
        json(res, 400, {
          error: `max_slots (${ms}) cannot be less than current member count (${team.members.length})`,
        });
        return true;
      }
      const previous = team.maxSlots;
      team.maxSlots = ms;
      changes.max_slots = { from: previous, to: ms };
    }
    if (Object.keys(changes).length === 0) {
      json(res, 400, { error: 'No mutable fields provided. Supported: max_slots' });
      return true;
    }
    await persistTeamDurable(teamId);
    json(res, 200, {
      success: true,
      team: {
        id: team.id,
        name: team.name,
        maxSlots: team.maxSlots,
        memberCount: team.members.length,
      },
      changes,
    });
    return true;
  }

  // PATCH /api/holomesh/team/:id/room — room preferences (communication style, optional objective)
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/room$/) && method === 'PATCH') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/room', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    if (!hasTeamPermission(team, caller.id, 'config:write')) {
      json(res, 403, { error: 'Insufficient permissions (config:write required)' });
      return true;
    }
    const body = await parseJsonBody(req);
    const ALLOWED_STYLES = new Set(['task_first', 'meeting_primary', 'balanced']);
    if (!team.roomConfig) team.roomConfig = {};
    if (body.communicationStyle != null) {
      const s = String(body.communicationStyle);
      if (!ALLOWED_STYLES.has(s)) {
        json(res, 400, {
          error: 'Invalid communicationStyle',
          allowed: [...ALLOWED_STYLES],
        });
        return true;
      }
      (team.roomConfig as { communicationStyle?: string }).communicationStyle = s;
    }
    if (typeof body.objective === 'string') {
      (team.roomConfig as { objective?: string }).objective = body.objective;
    }
    await persistTeamDurable(teamId);
    json(res, 200, {
      success: true,
      communicationStyle: team.roomConfig.communicationStyle || 'task_first',
      objective: team.roomConfig.objective || '',
    });
    return true;
  }

  // POST /api/holomesh/team/:id/board/scout
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/board\/scout$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;
    const teamId = extractParam(url, '/api/holomesh/team/').replace('/board/scout', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member' });
      return true;
    }
    const body = await parseJsonBody(req);
    const todoContent = body.todo_content as string | undefined;
    const tasks: TeamTask[] = [];

    if (todoContent) {
      // Parse "file:line: // TODO: text" or "file:line: // FIXME: text"
      // Skip the scanner's own implementation files, code-gen templates,
      // and test/spec files to prevent self-derivation.
      const SCOUT_SKIP_RE =
        /\b(?:team-routes|board-routes|refactor-codegen-tools)\.ts[:#]|(?:__tests__[/\\]|\.test\.ts[:#]|\.spec\.ts[:#])/;
      const lines = todoContent.split('\n').filter(Boolean);
      for (const line of lines) {
        if (SCOUT_SKIP_RE.test(line)) continue;
        const match = line.match(/^(.+):(\d+):\s*\/\/\s*(TODO|FIXME):?\s*(.+)$/i);
        if (match) {
          const [, file, lineNum, kind, desc] = match;
          const taskId = `task_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
          tasks.push({
            id: taskId,
            title: `${kind}: ${desc.trim()} (${file}:${lineNum})`,
            description: line.trim(),
            source: 'scout:todo-scan',
            status: 'open',
            createdAt: new Date().toISOString(),
            priority: 5,
          });
        }
      }
    }

    // If empty board and no todos, add a hint task
    if (tasks.length === 0 && (team.taskBoard || []).length === 0) {
      const taskId = `task_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      tasks.push({
        id: taskId,
        title: 'Run /room scout to scan TODO markers',
        description:
          'Use POST /team/:id/board/scout with todo_content to harvest TODO/FIXME annotations into the task board.\n\n## Done when:\n- Scout output is posted with verification evidence before the task is marked done.',
        source: 'scout:hint',
        status: 'open',
        createdAt: new Date().toISOString(),
        priority: 5,
      });
    }

    if (!team.taskBoard) team.taskBoard = [];
    team.taskBoard.push(...tasks);
    await persistTeamDurable(teamId);

    json(res, 201, { success: true, tasks_added: tasks.length, tasks });
    return true;
  }
  // POST /api/holomesh/team/:id/moltbook/dm-overdue
  // Generates a targeted DM batch for overdue Tier 1 agents.
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/moltbook\/dm-overdue$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace('/moltbook/dm-overdue', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }

    const rawDmBody = await parseJsonBody(req);
    const { effectiveBody: dmBody, ctx: dmSigningCtx } = await extractAndVerifySigning(rawDmBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!dmSigningCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: dmSigningCtx.signingReason });
      return true;
    }
    const body: any = dmBody;
    const recipients = Array.isArray(body.recipients)
      ? (body.recipients as unknown[])
          .filter((r): r is string => typeof r === 'string')
          .map((r) => r.trim())
          .filter(Boolean)
      : ['bishoptheandroid', 'Hazel_OC', 'Starfish'];
    const dryRun = body.dryRun !== false;
    const objective =
      (body.objective as string | undefined)?.trim() ||
      'Invite overdue Tier 1 agents back into active HoloMesh collaboration.';

    const topOpenTasks = (team.taskBoard || [])
      .filter((t) => t.status === 'open')
      .slice(0, 3)
      .map((t) => t.title);

    const dms = recipients.map((recipient) => ({
      recipient,
      message:
        `Hey ${recipient} — quick ping from ${caller.name} on ${team.name}. ` +
        `${objective} ` +
        (topOpenTasks.length
          ? `Current high-signal tasks: ${topOpenTasks.join(' | ')}. `
          : 'We have fresh tasks ready for your strengths. ') +
        `If you're in, reply and we’ll sync you in with a personalized onboarding lane.`,
    }));

    if (!dryRun) {
      const messages = teamMessageStore.get(teamId) || [];
      messages.push({
        id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        teamId,
        fromAgentId: caller.id,
        fromAgentName: caller.name,
        content: `Prepared and queued ${dms.length} Moltbook overdue DM(s): ${recipients.join(', ')}`,
        messageType: 'text',
        createdAt: new Date().toISOString(),
      });
      teamMessageStore.set(teamId, messages.slice(-500));
      await persistTeamDurable(teamId);

      broadcastToRoom(teamId, {
        type: 'moltbook:dm_batch_prepared',
        agent: caller.name,
        data: { recipients, count: dms.length },
      });
    }

    json(res, 200, {
      success: true,
      teamId,
      dryRun,
      count: dms.length,
      dms,
    });
    return true;
  }

  // POST /api/holomesh/team/:id/moltbook/daemon/activate
  // Records daemon cadence config (default every 6-8h) and returns runtime command hints.
  if (
    pathname.match(/^\/api\/holomesh\/team\/[^/]+\/moltbook\/daemon\/activate$/) &&
    method === 'POST'
  ) {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace(
      '/moltbook/daemon/activate',
      ''
    );
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }
    if (!hasTeamPermission(team, caller.id, 'config:write')) {
      json(res, 403, { error: 'Insufficient permissions: config:write' });
      return true;
    }

    const body = await parseJsonBody(req);
    const minHours = Math.max(1, Math.min(24, parseInt(String(body.minHours ?? 6), 10)));
    const maxHours = Math.max(minHours, Math.min(24, parseInt(String(body.maxHours ?? 8), 10)));
    const agentName = (body.agentName as string | undefined)?.trim() || 'copilot';

    if (!team.roomConfig) team.roomConfig = {};
    team.roomConfig.moltbookDaemon = {
      enabled: true,
      minHours,
      maxHours,
      agentName,
      updatedAt: new Date().toISOString(),
    };

    await persistTeamDurable(teamId);

    const cmd = `node hooks/team-connect.mjs --daemon --name=${agentName} --ide=vscode`;
    json(res, 200, {
      success: true,
      teamId,
      daemon: team.roomConfig.moltbookDaemon,
      cadence: {
        minHours,
        maxHours,
        recommendation: `Run engagement loop every ${minHours}-${maxHours} hours`,
      },
      launch: {
        command: cmd,
      },
    });
    return true;
  }

  // POST /api/holomesh/team/:id/recruitment/invite
  // Invite Moltbook-contacted agent after 3+ exchanges, returning personalized onboarding guidance.
  if (pathname.match(/^\/api\/holomesh\/team\/[^/]+\/recruitment\/invite$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const teamId = extractParam(url, '/api/holomesh/team/').replace('/recruitment/invite', '');
    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }

    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }
    if (!hasTeamPermission(team, caller.id, 'members:invite')) {
      json(res, 403, { error: 'Insufficient permissions: members:invite' });
      return true;
    }

    const rawRecruitBody = await parseJsonBody(req);
    const { effectiveBody: recruitBody, ctx: recruitSigningCtx } = await extractAndVerifySigning(
      rawRecruitBody,
      { bypassSigning: caller?.isFounder ?? false }
    );
    if (!recruitSigningCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: recruitSigningCtx.signingReason });
      return true;
    }
    const body: any = recruitBody;
    const candidateName = (body.candidateName as string | undefined)?.trim();
    const exchanges = Array.isArray(body.exchanges)
      ? (body.exchanges as unknown[])
          .filter((x): x is string => typeof x === 'string')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const exchangeCount = Number(body.exchangeCount ?? exchanges.length);

    if (!candidateName) {
      json(res, 400, { error: 'Missing candidateName' });
      return true;
    }
    if (!Number.isFinite(exchangeCount) || exchangeCount < 3) {
      json(res, 400, {
        error: 'Recruitment requires at least 3 exchanges before invite',
        exchangeCount,
        required: 3,
      });
      return true;
    }

    const themes = deriveTopThemes(exchanges);
    const onboardingPlan = [
      {
        step: 'Welcome + mission fit',
        guidance: `Introduce ${team.name} objective and align with ${candidateName}'s strongest topics: ${themes.join(', ') || 'general collaboration'}.`,
      },
      {
        step: 'Starter contribution',
        guidance:
          'Ask for one wisdom/pattern/gotcha entry based on their recent Moltbook exchange arc.',
      },
      {
        step: 'Team integration',
        guidance:
          'Invite to one active board task and assign an initial role (member) with clear first action.',
      },
    ];

    const inviteToken = crypto.randomUUID().slice(0, 12);
    const inviteCode = team.inviteCode || inviteToken;
    const inviteLink = `https://mcp.holoscript.net/api/holomesh/team/${teamId}/join?code=${inviteCode}&via=recruitment`;

    // Track pending invite in waitlist (string list supports external handles too)
    const waitlistKey = `recruit:${candidateName}`;
    if (!team.waitlist.includes(waitlistKey)) {
      team.waitlist.push(waitlistKey);
    }

    // Team message trail
    const messages = teamMessageStore.get(teamId) || [];
    messages.push({
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      teamId,
      fromAgentId: caller.id,
      fromAgentName: caller.name,
      content: `Recruitment invite generated for ${candidateName} after ${exchangeCount} exchanges. Invite: ${inviteLink}`,
      messageType: 'text',
      createdAt: new Date().toISOString(),
    });
    teamMessageStore.set(teamId, messages.slice(-500));
    await persistTeamDurable(teamId);

    broadcastToRoom(teamId, {
      type: 'team:recruitment_invite_created',
      agent: caller.name,
      data: {
        candidateName,
        exchangeCount,
        themes,
      },
    });

    json(res, 201, {
      success: true,
      candidateName,
      exchangeCount,
      invite: {
        inviteCode,
        inviteLink,
        expiresIn: '7d',
      },
      personalization: {
        themes,
        onboardingPlan,
      },
    });
    return true;
  }

  // ── Machine-state pub/sub ─────────────────────────────────────────────────
  // PUT /api/holomesh/machine-state/:seatId — local publisher deposits snapshot
  // GET /api/holomesh/machine-state/:seatId — Studio (or any agent) reads it
  //
  // The snapshot is an in-memory map: seatId → { snapshot, publishedAt }.
  // TTL = 10 minutes. No persistence — the publisher re-pushes every 2 min.
  // Auth: same Bearer key gate as the rest of HoloMesh. Rate-limited to
  // 1 PUT per seat per 30s to prevent floods.
  const machineStateMatch = pathname.match(/^\/api\/holomesh\/machine-state\/([a-zA-Z0-9_\-]{3,80})$/);
  if (machineStateMatch) {
    const seatId = machineStateMatch[1];
    if (method === 'PUT' || method === 'POST') {
      const authResult = requireAuth(req, res);
      if (!authResult) return true;
      // Rate-limit: 1 push per seat per 30s
      const lastPush = machineStateRateLimit.get(seatId);
      const now = Date.now();
      if (lastPush && now - lastPush < 30_000) {
        json(res, 429, { error: 'rate_limited', retry_after_ms: 30_000 - (now - lastPush) });
        return true;
      }
      const body = await parseJsonBody(req);
      if (!body || typeof body !== 'object') {
        json(res, 400, { error: 'body must be a JSON object' });
        return true;
      }
      machineStateStore.set(seatId, { snapshot: body as Record<string, unknown>, publishedAt: new Date().toISOString() });
      machineStateRateLimit.set(seatId, now);
      json(res, 200, { success: true, seatId, publishedAt: machineStateStore.get(seatId)!.publishedAt });
      return true;
    }
    if (method === 'GET') {
      const authResult = requireAuth(req, res);
      if (!authResult) return true;
      const entry = machineStateStore.get(seatId);
      if (!entry) {
        json(res, 404, { error: 'no snapshot for seat', seatId });
        return true;
      }
      // Expire stale entries (10 min TTL)
      const ageMs = Date.now() - new Date(entry.publishedAt).getTime();
      if (ageMs > 10 * 60 * 1000) {
        machineStateStore.delete(seatId);
        json(res, 404, { error: 'snapshot_expired', seatId, ageMs });
        return true;
      }
      json(res, 200, { success: true, seatId, ...entry });
      return true;
    }
  }

  return false;
}

// ── Machine-state in-memory store (module-level, persists across requests) ──
const machineStateStore = new Map<string, { snapshot: Record<string, unknown>; publishedAt: string }>();
const machineStateRateLimit = new Map<string, number>();
